import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { Alert, Snackbar } from '@mui/material';
import ReportToolbar from './ReportToolbar';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmt = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
const fmtQty = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const PurchaseInvoiceRegister = () => {
    useSessionValidation();
    const history = useHistory();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();

    const today = new Date();
    const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate] = useState(fmt2Date(today));
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fetched, setFetched] = useState(false);
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState({});

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'PurchaseReportApi',
                `/PurchaseInvoiceRegistrationDetails/${companyId}/${branchId}/${fromDate}/${toDate}`,
                token
            );
            const rows = res.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
            setExpanded({});
        } catch (err) {
            console.error(err);
            setError('Failed to fetch purchase invoice register');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (companyId && branchId && token) fetchReport();
    }, [companyId, branchId, token]);

    // Group flat rows: master row has BillNumber, item rows have ItemName
    const masters = useMemo(() => {
        return data.reduce((acc, row) => {
            if (row.BillNumber) {
                acc.push({
                    PurchaseId: row.PurchaseId,
                    BillNumber: row.BillNumber,
                    BillDate: row.BillDate,
                    SupplierName: row.SupplierName,
                    GSTNumber: row.GSTNumber,
                    PONumber: row.PONumber,
                    PODate: row.PODate,
                    TotalQty: row.TotalQty,
                    GrossAmount: row.GrossAmount,
                    TotalDiscount: row.TotalDiscount,
                    InvoiceTaxable: row.InvoiceTaxable,
                    InvoiceCGST: row.InvoiceCGST,
                    InvoiceSGST: row.InvoiceSGST,
                    InvoiceIGST: row.InvoiceIGST,
                    InvoiceTotal: row.InvoiceTotal,
                    PaidAmount: row.PaidAmount,
                    BalanceAmount: row.BalanceAmount,
                    items: [],
                });
            } else if (row.ItemName && acc.length > 0) {
                acc[acc.length - 1].items.push({
                    ItemName: row.ItemName,
                    BatchNumber: row.BatchNumber,
                    HSNCode: row.HSNCode,
                    Quantity: row.Quantity,
                    Rate: row.Rate,
                    Amount: row.Amount,
                    Discount: row.Discount,
                    TaxableAmount: row.TaxableAmount,
                    CGST: row.CGST,
                    SGST: row.SGST,
                    IGST: row.IGST,
                    ItemTotal: row.ItemTotal,
                });
            }
            return acc;
        }, []);
    }, [data]);

    // Filter by search
    const filtered = useMemo(() => {
        if (!search.trim()) return masters;
        const q = search.toLowerCase();
        return masters.filter(m =>
            (m.BillNumber || '').toLowerCase().includes(q) ||
            (m.SupplierName || '').toLowerCase().includes(q) ||
            (m.PONumber || '').toLowerCase().includes(q)
        );
    }, [masters, search]);

    const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

    // Totals
    const totalTaxable = filtered.reduce((s, r) => s + (parseFloat(r.InvoiceTaxable) || 0), 0);
    const totalGST = filtered.reduce((s, r) => s + (parseFloat(r.InvoiceCGST) || 0) + (parseFloat(r.InvoiceSGST) || 0) + (parseFloat(r.InvoiceIGST) || 0), 0);
    const totalAmount = filtered.reduce((s, r) => s + (parseFloat(r.InvoiceTotal) || 0), 0);
    const totalQty = filtered.reduce((s, r) => s + (parseFloat(r.TotalQty) || 0), 0);
    const totalPaid = filtered.reduce((s, r) => s + (parseFloat(r.PaidAmount) || 0), 0);
    const totalBalance = filtered.reduce((s, r) => s + (parseFloat(r.BalanceAmount) || 0), 0);

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'PurchaseInvoiceRegister', 0);
        if (!filtered.length) return;
        const colHeaders = ['#', 'Invoice No', 'Date', 'Supplier', 'Supplier Inv No', 'Total Qty', 'Taxable Amt', 'GST', 'Total Amt', 'Verified', '', 'Item Name', 'Qty', 'Rate', 'GST%', 'Taxable', 'Item GST', 'Item Total'];
        const rows = [];
        filtered.forEach((m, i) => {
            rows.push([i + 1, m.PurchaseNo, fmtDate(m.PurchaseDate), m.MasterName, m.InvoiceNo,
            parseFloat(m.TotalQuantity) || 0, parseFloat(m.TaxableAmount) || 0,
            parseFloat(m.TotalGST) || 0, parseFloat(m.TotalAmount) || 0,
            m.IsVerified === 'Yes' || m.IsVerified === true ? 'Yes' : 'No',
                '', '', '', '', '', '', '', '']);
            m.items.forEach(it => {
                rows.push(['', '', '', '', '', '', '', '', '', '', '',
                    it.ItemName, parseFloat(it.Quantity) || 0, parseFloat(it.Rate) || 0,
                    parseFloat(it.GSTRate) || 0, parseFloat(it.TaxableAmount) || 0,
                    parseFloat(it.ItemGST) || 0, parseFloat(it.TotalAmount) || 0]);
            });
        });

        await exportStyledExcel({
            reportTitle: 'PURCHASE INVOICE REGISTER',
            sheetName: 'Purchase Invoice Register',
            fileName: `PurchaseInvoiceRegister_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: colHeaders,
            rows,
            totalsRow: ['', '', '', '', 'Totals', totalQty, totalTaxable, totalGST, totalAmount, '', '', '', '', '', '', '', '', ''],
        });
    };

    // Styles
    const th = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
    const td = { padding: '7px 10px', textAlign: 'left', fontSize: 12, whiteSpace: 'nowrap' };
    const tdR = { padding: '7px 10px', textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap' };
    const tdC = { padding: '7px 10px', textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' };

    const PRIMARY = '#0d8392';
const PRIMARY_MID = '#1aabb9';
const PRIMARY_LT = '#e0f7fa';

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading purchase invoice register..." />

            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <ReportToolbar
                    title="Purchase Invoice Register"
                    onBack={() => history.push('/ReportsHub')}
                    search={search}
                    onSearchChange={setSearch}
                    fromDate={fromDate}
                    onFromDate={setFromDate}
                    toDate={toDate}
                    onToDate={setToDate}
                    onRefresh={fetchReport}
                    loading={loading}
                    onExport={handleExport}
                    exportDisabled={!filtered.length}
                    searchPlaceholder="Search invoice, supplier..."
                />

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 900 }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                            <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                                <th style={{ ...th, width: 32 }}></th>
                                <th style={th}>#</th>
                                <th style={th}>Bill No</th>
                                <th style={th}>Bill Date</th>
                                <th style={th}>Supplier</th>
                                <th style={th}>GST No</th>
                                <th style={th}>PO No</th>
                                <th style={{ ...th, textAlign: 'right' }}>Total Qty</th>
                                <th style={{ ...th, textAlign: 'right' }}>Gross Amt</th>
                                <th style={{ ...th, textAlign: 'right' }}>Discount</th>
                                <th style={{ ...th, textAlign: 'right' }}>Taxable</th>
                                <th style={{ ...th, textAlign: 'right' }}>CGST</th>
                                <th style={{ ...th, textAlign: 'right' }}>SGST</th>
                                <th style={{ ...th, textAlign: 'right' }}>IGST</th>
                                <th style={{ ...th, textAlign: 'right' }}>Total</th>
                                <th style={{ ...th, textAlign: 'right' }}>Paid</th>
                                <th style={{ ...th, textAlign: 'right' }}>Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={11} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click refresh'}
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((m, i) => {
                                    const isOpen = !!expanded[m.BillNumber];
                                    return (
                                        <>
                                            {/* Master Row */}
                                            <tr
                                                key={m.BillNumber}
                                                onClick={() => toggleExpand(m.BillNumber)}
                                                style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                                             >
                                                <td style={{ ...tdC, width: 32 }}>
                                                    {isOpen
                                                        ? <KeyboardArrowDown sx={{ fontSize: 16, color: PRIMARY }} />
                                                        : <KeyboardArrowRight sx={{ fontSize: 16, color: '#94a3b8' }} />}
                                                </td>
                                                <td style={td}>{i + 1}</td>
                                                <td style={{ ...td, fontWeight: 700, color: PRIMARY }}>{m.BillNumber}</td>
                                                <td style={td}>{fmtDate(m.BillDate)}</td>
                                                <td style={{ ...td, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.SupplierName}</td>
                                                <td style={{ ...td, color: '#64748b', fontSize: 11 }}>{m.GSTNumber || '-'}</td>
                                                <td style={{ ...td, color: '#64748b' }}>{m.PONumber || '-'}</td>
                                                <td style={tdR}>{fmtQty(m.TotalQty)}</td>
                                                <td style={tdR}>{fmt(m.GrossAmount)}</td>
                                                <td style={{ ...tdR, color: '#dc2626' }}>{fmt(m.TotalDiscount)}</td>
                                                <td style={tdR}>{fmt(m.InvoiceTaxable)}</td>
                                                <td style={tdR}>{fmt(m.InvoiceCGST)}</td>
                                                <td style={tdR}>{fmt(m.InvoiceSGST)}</td>
                                                <td style={tdR}>{fmt(m.InvoiceIGST)}</td>
                                                <td style={{ ...tdR, fontWeight: 700 }}>{fmt(m.InvoiceTotal)}</td>
                                                <td style={{ ...tdR, color: '#059669', fontWeight: 600 }}>{fmt(m.PaidAmount)}</td>
                                                <td style={{ ...tdR, color: (parseFloat(m.BalanceAmount) || 0) > 0 ? '#dc2626' : '#374151', fontWeight: 600 }}>{fmt(m.BalanceAmount)}</td>
                                            </tr>

                                            {/* Item Sub-Header */}
                                            {isOpen && (
                                                <tr style={{ background: '#dbeafe', borderBottom: '1px solid #bfdbfe' }}>
                                                    <td style={tdC}></td>
                                                    <td style={{ ...td, fontWeight: 700, fontSize: 11, color: PRIMARY }}>#</td>
                                                    <td style={{ ...td, fontWeight: 700, fontSize: 11, color: PRIMARY, paddingLeft: 24 }}>Item Name</td>
                                                    <td style={{ ...td, fontWeight: 700, fontSize: 11, color: PRIMARY }}>Batch</td>
                                                    <td style={{ ...td, fontWeight: 700, fontSize: 11, color: PRIMARY }}>HSN</td>
                                                    <td style={{ ...td, fontWeight: 700, fontSize: 11, color: PRIMARY }}></td>
                                                    <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: PRIMARY }}>Qty</td>
                                                    <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: PRIMARY }}>Rate</td>
                                                    <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: PRIMARY }}>Amount</td>
                                                    <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: PRIMARY }}>Discount</td>
                                                    <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: PRIMARY }}>Taxable</td>
                                                    <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: PRIMARY }}>CGST</td>
                                                    <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: PRIMARY }}>SGST</td>
                                                    <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: PRIMARY }}>IGST</td>
                                                    <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: PRIMARY }}>Total</td>
                                                    <td colSpan={3} style={td}></td>
                                                </tr>
                                            )}

                                            {/* Item Rows */}
                                            {isOpen && m.items.map((it, j) => (
                                                <tr key={`${m.BillNumber}-${j}`} style={{ background: '#eff6ff', borderBottom: '1px solid #dbeafe' }}>
                                                    <td style={tdC}></td>
                                                    <td style={{ ...td, color: '#64748b' }}>{j + 1}</td>
                                                    <td style={{ ...td, fontWeight: 600, color: '#1e293b', paddingLeft: 24 }}>{it.ItemName}</td>
                                                    <td style={{ ...td, color: '#64748b', fontSize: 11 }}>{it.BatchNumber || '-'}</td>
                                                    <td style={{ ...td, color: '#64748b', fontSize: 11 }}>{it.HSNCode || '-'}</td>
                                                    <td style={{ ...td, color: '#64748b', fontSize: 11 }}></td>
                                                    <td style={tdR}>{fmtQty(it.Quantity)}</td>
                                                    <td style={tdR}>{fmt(it.Rate)}</td>
                                                    <td style={tdR}>{fmt(it.Amount)}</td>
                                                    <td style={{ ...tdR, color: '#dc2626' }}>{fmt(it.Discount)}</td>
                                                    <td style={tdR}>{fmt(it.TaxableAmount)}</td>
                                                    <td style={tdR}>{fmt(it.CGST)}</td>
                                                    <td style={tdR}>{fmt(it.SGST)}</td>
                                                    <td style={tdR}>{fmt(it.IGST)}</td>
                                                    <td style={{ ...tdR, fontWeight: 600 }}>{fmt(it.ItemTotal)}</td>
                                                    <td colSpan={3} style={td}></td>
                                                </tr>
                                            ))}
                                        </>
                                    );
                                })
                            )}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, color: PRIMARY, fontWeight: 700 }}>
                                    <td colSpan={7} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                                    <td style={tdR}>{fmtQty(totalQty)}</td>
                                    <td style={tdR}></td>
                                    <td style={tdR}></td>
                                    <td style={tdR}>{fmt(totalTaxable)}</td>
                                    <td style={tdR}></td>
                                    <td style={tdR}></td>
                                    <td style={tdR}></td>
                                    <td style={tdR}>{fmt(totalAmount)}</td>
                                    <td style={tdR}>{fmt(totalPaid)}</td>
                                    <td style={tdR}>{fmt(totalBalance)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PurchaseInvoiceRegister;


